import { estRefPortee, porteesDuGroupe, reglagesDuGroupe, type SchemaReglages } from '../reglages/modele.js'
import { CODE_SALLE } from './fiches/modele.js'
import { renommerSalles } from './salles.js'

/**
 * MENU CLIC DROIT D'UNE SALLE (lot 2, 24/10/2026) — calculs purs, testés
 * sans navigateur (tests/menu-salle.test.ts). Le menu lui-même vit dans
 * `menu-salle.tsx`.
 *
 * Chaque destination ci-dessous est une page qui existe déjà, ouverte par
 * un lien qui existe déjà : le menu ne fait que gagner un clic.
 */

/** Les sections de la fiche salle (FicheSallePage.tsx), par leur `id`. */
export const ANCRES_SALLE = {
  incidents: 'sv-sal-inc',
  appareils: 'sv-sal-familles',
  alimentation: 'sv-sal-alim',
  score: 'sv-sal-score',
} as const

/** Technique › Audiovisuel : son et lumière, salle par salle (App.tsx, `sonde.read`). */
export const CHEMIN_AUDIOVISUEL = '/technique/audiovisuel'
/**
 * Sécurité › Vidéosurveillance, section « Caméras » : TOUTES les caméras,
 * regroupées par salle. Le filtre sur une seule salle n'a pas d'adresse
 * (état local de VideoPage) : le menu ne prétend pas filtrer.
 */
export const CHEMIN_VIDEO = '/securite/videosurveillance'
export const ANCRE_CAMERAS = 'sv-vid-cameras'
/** Le Mode soirée, plein écran de la régie (bouton du hub). */
export const CHEMIN_MODE_SOIREE = '/infrastructure/live'
/** Paramètres › Supervision (le lien de la fiche salle, sous `sonde.settings.manage`). */
export const CHEMIN_REGLAGES = '/settings/supervision'

/** Le code d'un écran de réglages que la route accepte (App.tsx, `supGroupe`). */
const ROUTE_GROUPE = /^[a-z][a-z0-9_]{1,40}$/

/** Un code de salle que la fiche accepte ; sinon pas de menu (la fiche dirait « adresse invalide »). */
export const estCodeSalle = (code: string | null | undefined): code is string => !!code && CODE_SALLE.test(code.toUpperCase())

/**
 * L'écran d'un groupe de réglages, ouvert à la portée d'une salle : la même
 * adresse que celle que l'écran écrit lui-même quand on y choisit la salle
 * (`?portee=zone:<code>`, SupervisionGroupePage.tsx, `ecrirePorteeUrl`).
 */
export function cheminReglageSalle(groupe: string, code: string): string {
  return `${CHEMIN_REGLAGES}/${groupe}?${new URLSearchParams({ portee: `zone:${code}` }).toString()}`
}

export interface EcranReglage { code: string; libelle: string; href: string }

/**
 * Les écrans de réglages qui acceptent une portée « salle », dans l'ordre de
 * Sonde (`screen`). Mêmes calculs que l'écran du groupe : un groupe propose
 * la portée `zone` si un de ses réglages la déclare (`porteesDuGroupe`).
 * Écartés : le groupe `source` (réglé source par source, sans portée), un
 * code que la route refuserait, une salle que la portée refuserait. Le
 * libellé est celui de Sonde, anciens noms de salle remplacés.
 */
export function ecransReglagesSalle(schema: SchemaReglages | null, code: string): EcranReglage[] {
  if (!schema || !estRefPortee(code)) return []
  return [...schema.groups]
    .filter((g) => g.code !== 'source' && ROUTE_GROUPE.test(g.code))
    .filter((g) => porteesDuGroupe(reglagesDuGroupe(schema, g.code)).includes('zone'))
    .sort((a, b) => a.screen - b.screen || a.code.localeCompare(b.code))
    .map((g) => ({ code: g.code, libelle: renommerSalles(g.label) || g.code, href: cheminReglageSalle(g.code, code) }))
}

/**
 * Le droit qu'exige la page d'une tuile de domaine du hub (App.tsx,
 * `RequirePermission`) : le registre demande `sonde.details.read`, tout le
 * reste `sonde.read`. Le menu n'offre pas une page qui refuserait le compte.
 */
export function droitDestination(href: string): 'sonde.read' | 'sonde.details.read' {
  const chemin = href.split(/[?#]/)[0] ?? href
  return chemin === '/infrastructure/registre' ? 'sonde.details.read' : 'sonde.read'
}
